import { Controller } from "@hotwired/stimulus"

export default class extends Controller {
    static targets = ["emailPanel", "overlay", "passwordPanel", "passwordOverlay"]

    connect() {
        // Validation listeners will be attached when panels open
    }
    
    disconnect() {
        try {
            if (this.hasEmailPanelTarget) {
                this.emailPanelTarget.style.right = '-100%'
            }
            if (this.hasOverlayTarget) {
                this.overlayTarget.classList.add('hidden')
            }
            if (this.hasPasswordPanelTarget) {
                this.passwordPanelTarget.style.right = '-100%'
            }
            if (this.hasPasswordOverlayTarget) {
                this.passwordOverlayTarget.classList.add('hidden')
            }
        } catch (error) {
            console.warn('Could not reset panel or overlay state during disconnect')
        }
        
        document.body.style.overflow = ''
    }

    handleEmailSubmit(event) {
        // Validate email fields before submission
        const form = event.target
        const newEmail = form.querySelector('#id_new_email').value.trim()
        const confirmEmail = form.querySelector('#id_confirm_email').value.trim()
        
        // Clear any existing error messages
        this.clearErrorMessage(form)
        
        // Validate email format
        if (!this.isValidEmail(newEmail)) {
            this.showErrorMessage(form, 'Please enter a valid email address.')
            this.resetSubmitButton(form, 'Save')
            event.preventDefault()
            return
        }
        
        // Validate emails match
        if (newEmail !== confirmEmail) {
            this.showErrorMessage(form, 'Email addresses do not match.')
            this.resetSubmitButton(form, 'Save')
            event.preventDefault()
            return
        }
        
        // Show loading state
        const submitButton = form.querySelector('button[type="submit"]')
        if (submitButton) {
            submitButton.innerHTML = 'Updating Email...'
            submitButton.disabled = true
        }
        
        // Let Turbo handle the form submission
        // The backend will return a Turbo Stream response
    }

    handlePasswordSubmit(event) {
        // Validate password fields before submission
        const form = event.target
        const currentPassword = form.querySelector('#id_current_password').value.trim()
        const newPassword = form.querySelector('#id_new_password').value.trim()
        const confirmPassword = form.querySelector('#id_confirm_password').value.trim()
        
        // Clear any existing error messages
        this.clearErrorMessage(form)
        
        // Validate required fields
        if (!currentPassword) {
            this.showErrorMessage(form, 'Current password is required.')
            this.resetSubmitButton(form, 'Save')
            event.preventDefault()
            return
        }
        
        if (!newPassword) {
            this.showErrorMessage(form, 'New password is required.')
            this.resetSubmitButton(form, 'Save')
            event.preventDefault()
            return
        }
        
        // Validate password strength
        const passwordValidation = this.validatePasswordStrength(newPassword)
        if (!passwordValidation.valid) {
            this.showErrorMessage(form, passwordValidation.message)
            this.resetSubmitButton(form, 'Save')
            event.preventDefault()
            return
        }
        
        // Validate passwords match
        if (newPassword !== confirmPassword) {
            this.showErrorMessage(form, 'Passwords do not match.')
            this.resetSubmitButton(form, 'Save')
            event.preventDefault()
            return
        }
        
        // Show loading state
        const submitButton = form.querySelector('button[type="submit"]')
        if (submitButton) {
            submitButton.innerHTML = 'Changing Password...'
            submitButton.disabled = true
        }
        
        // Let Turbo handle the form submission
        // The backend will return a Turbo Stream response
    }

    // Validation helper methods
    isValidEmail(email) {
        const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/
        return emailRegex.test(email)
    }

    validatePasswordStrength(password) {
        if (password.length < 8) {
            return { valid: false, message: 'Password must be at least 8 characters long.' }
        }
        if (!/[a-z]/.test(password)) {
            return { valid: false, message: 'Password must contain at least one lowercase letter.' }
        }
        if (!/[A-Z]/.test(password)) {
            return { valid: false, message: 'Password must contain at least one uppercase letter.' }
        }
        if (!/[0-9]/.test(password)) {
            return { valid: false, message: 'Password must contain at least one number.' }
        }
        if (!/[!@#$%^&*(),.?":{}|<>]/.test(password)) {
            return { valid: false, message: 'Password must contain at least one special character.' }
        }
        return { valid: true }
    }

    showErrorMessage(form, message) {
        // Find or create error container for form submission errors
        let errorContainer = form.querySelector('.frontend-validation-error')
        if (!errorContainer) {
            errorContainer = document.createElement('div')
            errorContainer.className = 'frontend-validation-error w-full max-w-[540px] bg-[#FFEBEE] border border-[#D32F2F] rounded-[4px] py-3 px-4 mt-2'
            
            // Find the fields container by looking for the div that contains the input fields
            const fieldsContainer = Array.from(form.querySelectorAll('div')).find(div => 
                div.classList.contains('w-full') && 
                div.classList.contains('flex') && 
                div.classList.contains('flex-col') && 
                div.querySelector('input')
            )
            
            if (fieldsContainer) {
                // Insert after the fields container
                form.insertBefore(errorContainer, fieldsContainer.nextSibling)
            } else {
                // Fallback: append to the end of form
                form.appendChild(errorContainer)
            }
        }
        
        errorContainer.innerHTML = `
            <div class="flex items-start gap-3">
                <span class="w-5 h-5 text-[#D32F2F] flex-shrink-0 mt-0.5">
                    <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                    </svg>
                </span>
                <p class="text-sm text-[#D32F2F] font-medium">${message}</p>
            </div>
        `
        errorContainer.style.display = 'block'
    }

    clearErrorMessage(form) {
        const errorContainer = form.querySelector('.frontend-validation-error')
        if (errorContainer) {
            errorContainer.style.display = 'none'
        }
    }

    resetSubmitButton(form, originalText) {
        const submitButton = form.querySelector('button[type="submit"]')
        if (submitButton) {
            submitButton.innerHTML = originalText
            submitButton.disabled = false
        }
    }

    resetEmailForm() {
        const emailForm = document.getElementById('edit-email-form')
        if (!emailForm) return
        
        const newEmailInput = emailForm.querySelector('#id_new_email')
        const confirmEmailInput = emailForm.querySelector('#id_confirm_email')
        
        if (newEmailInput) {
            newEmailInput.value = ''
        }
        if (confirmEmailInput) {
            confirmEmailInput.value = ''
        }
        
        this.clearErrorMessage(emailForm)
    }

    resetPasswordForm() {
        const passwordForm = document.getElementById('reset-password-form')
        if (!passwordForm) return
        
        const currentPasswordInput = passwordForm.querySelector('#id_current_password')
        const newPasswordInput = passwordForm.querySelector('#id_new_password')
        const confirmPasswordInput = passwordForm.querySelector('#id_confirm_password')
        
        if (currentPasswordInput) {
            currentPasswordInput.value = ''
        }
        if (newPasswordInput) {
            newPasswordInput.value = ''
        }
        if (confirmPasswordInput) {
            confirmPasswordInput.value = ''
        }
        
        this.clearErrorMessage(passwordForm)
    }

    // Real-time validation methods
    addEmailValidationListeners() {
        const emailForm = document.getElementById('edit-email-form')
        
        if (emailForm) {
            const newEmailInput = emailForm.querySelector('#id_new_email')
            const confirmEmailInput = emailForm.querySelector('#id_confirm_email')
            
            // Only add input listeners to clear backend errors when user starts typing
            if (newEmailInput) {
                newEmailInput.addEventListener('input', () => this.clearErrorMessage(emailForm))
            }
            if (confirmEmailInput) {
                confirmEmailInput.addEventListener('input', () => this.clearErrorMessage(emailForm))
            }
        }
    }

    addPasswordValidationListeners() {
        const passwordForm = document.getElementById('reset-password-form')
        
        if (passwordForm) {
            const currentPasswordInput = passwordForm.querySelector('#id_current_password')
            const newPasswordInput = passwordForm.querySelector('#id_new_password')
            const confirmPasswordInput = passwordForm.querySelector('#id_confirm_password')
            
            // Only add input listeners to clear backend errors when user starts typing
            if (currentPasswordInput) {
                currentPasswordInput.addEventListener('input', () => this.clearErrorMessage(passwordForm))
            }
            if (newPasswordInput) {
                newPasswordInput.addEventListener('input', () => this.clearErrorMessage(passwordForm))
            }
            if (confirmPasswordInput) {
                confirmPasswordInput.addEventListener('input', () => this.clearErrorMessage(passwordForm))
            }
        }
    }

    // Legacy method - kept for compatibility but split into separate methods above
    addValidationListeners() {
        this.addEmailValidationListeners()
        this.addPasswordValidationListeners()
    }

    toggleBackToTop(hide) {
        const backToTopContainer = document.getElementById('back-to-top-container')
        if (backToTopContainer) {
            backToTopContainer.classList.toggle('hidden', hide)
        }
    }

    closeSearchBar() {
        const navigationController = this.application.getControllerForElementAndIdentifier(
            document.querySelector('[data-controller="navigation"]'),
            "navigation"
        )
        
        if (navigationController && navigationController.hasSearchBarTarget) {
            const searchBar = navigationController.searchBarTarget
            
            if (searchBar && searchBar.style.display !== 'none') {
                navigationController.toggleSearchBar()
            }
        }
    }

    openEmail(event) {
        if (event) event.preventDefault()
        
        this.closeSearchBar()
        
        if (this.hasEmailPanelTarget) {
            this.emailPanelTarget.style.right = '0'
        }
        if (this.hasOverlayTarget) {
            this.overlayTarget.classList.remove('hidden')
        }
        document.body.style.overflow = 'hidden'
        this.toggleBackToTop(true)
        
        // Add validation listeners after panel is open
        setTimeout(() => {
            this.addEmailValidationListeners()
        }, 100)
    }

    closeEmail(event) {
        if (event) event.preventDefault()

        if (this.hasEmailPanelTarget) {
            this.emailPanelTarget.style.right = '-100%'
        }
        if (this.hasOverlayTarget) {
            this.overlayTarget.classList.add('hidden')
        }
        document.body.style.overflow = ''
        this.toggleBackToTop(false)
        this.resetEmailForm()
    }

    openPassword(event) {
        if (event) event.preventDefault()
        
        this.closeSearchBar()
        
        if (this.hasPasswordPanelTarget) {
            this.passwordPanelTarget.style.right = '0'
        }
        if (this.hasPasswordOverlayTarget) {
            this.passwordOverlayTarget.classList.remove('hidden')
        }
        document.body.style.overflow = 'hidden'
        this.toggleBackToTop(true)
        
        // Add validation listeners after panel is open
        setTimeout(() => {
            this.addPasswordValidationListeners()
        }, 100)
    }

    closePassword(event) {
        if (event) event.preventDefault()

        if (this.hasPasswordPanelTarget) {
            this.passwordPanelTarget.style.right = '-100%'
        }
        if (this.hasPasswordOverlayTarget) {
            this.passwordOverlayTarget.classList.add('hidden')
        }
        document.body.style.overflow = ''
        this.toggleBackToTop(false)
        this.resetPasswordForm()
    }
}
